<template>
  <div id="root" class="wizard-container">
    <div class="card card-wizard active" id="wizardProfile">
      <form @submit.prevent>
        <!--
          You can switch " data-color="primary" "  with one of the next bright colors: "green", "orange", "red", "blue"
        -->
        <div class="card-header text-center">
          <slot name="header" v-if="showHeader">
            <h3 v-if="title" class="card-title">{{ title }}</h3>
            <h5 v-if="subTitle" class="description">{{ subTitle }}</h5>
          </slot>

          <div class="wizard-navigation">
            <div class="progress-with-circle">
              <div
                class="progress-bar"
                role="progressbar"
                aria-valuenow="1"
                aria-valuemin="1"
                aria-valuemax="3"
                :style="{ width: `${progress}%` }"
              ></div>
            </div>
            <ul class="nav nav-pills">
              <li
                v-for="(tab, index) in tabs"
                :key="tab.title"
                role="tab"
                :tabindex="tab.checked ? 0 : ''"
                :id="`step-${tab.tabId}`"
                :aria-controls="tab.tabId"
                :aria-disabled="!tab.active"
                :aria-selected="tab.active"
                :ref="`tab-${index}`"
                class="nav-item wizard-tab-link"
                :style="linkWidth"
              >
                <a
                  class="nav-link"
                  @click="navigateToTab(index)"
                  :class="[
                    { 'disabled-wizard-link': !tab.checked || navigating },
                    { active: tab.active },
                    { checked: tab.checked }
                  ]"
                  data-toggle="tab"
                >
                  <tab-item-content :tab="tab"></tab-item-content>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div class="card-body">
          <div class="tab-content">
            <slot :activeIndex="activeTabIndex" :activeTab="activeTab"> </slot>
          </div>
        </div>

        <div class="card-footer">
          <slot name="footer" :next-tab="nextTab" :prev-tab="prevTab">
            <div v-show="showNextButton" class="wizard-footer-buttons">
              <base-button
                v-if="activeTabIndex > 0"
                type="primary"
                class="wiz-btn wiz-btn--prev"
                :disabled="navigating"
                @click.native="prevTab"
              >
                <i class="tim-icons icon-minimal-left"></i>
                {{ prevButtonText }}
              </base-button>
              <span v-else class="wiz-btn-spacer"></span>

              <div class="wiz-btn-right">
                <slot name="footer-right"></slot>
                <base-button
                  v-if="activeTabIndex < tabCount - 1"
                  type="primary"
                  class="wiz-btn wiz-btn--next"
                  :disabled="navigating"
                  @click.native="nextTab"
                >
                  <span v-if="navigating" class="wiz-spinner"></span>
                  <template v-else>
                    {{ nextButtonText }}
                    <i class="tim-icons icon-minimal-right"></i>
                  </template>
                </base-button>
                <base-button
                  v-else
                  type="primary"
                  class="wiz-btn wiz-btn--finish"
                  :disabled="navigating"
                  @click.native="nextTab"
                >
                  <span v-if="navigating" class="wiz-spinner"></span>
                  <template v-else>
                    {{ finishButtonText }}
                    <i class="tim-icons icon-check-2"></i>
                  </template>
                </base-button>
              </div>
            </div>
          </slot>
        </div>
      </form>
    </div>
  </div>
</template>
<script>
import { throttle } from './throttle';

export default {
  name: 'simple-wizard',
  props: {
    startIndex: {
      type: Number,
      default: 0
    },
    title: {
      type: String,
      default: 'Title'
    },
    subTitle: {
      type: String,
      default: 'Subtitle'
    },
    prevButtonText: {
      type: String,
      default: 'Previous'
    },
    showNextButton: {
      type: Boolean,
      default: true
    },
    nextButtonText: {
      type: String,
      default: 'Next'
    },
    finishButtonText: {
      type: String,
      default: 'Finish'
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    vertical: {
      type: Boolean
    }
  },
  components: {
    TabItemContent: {
      functional: true,
      props: ['tab'],
      render(h, {props}) {
        let content = props.tab.$slots.label;
        if(content && content.length){
          return content;
        }
        return h('span', [props.tab.$slots.label, props.tab.label])
      }
    }
  },
  provide() {
    return {
      addTab: this.addTab,
      removeTab: this.removeTab
    };
  },
  data() {
    return {
      tabs: [],
      activeTabIndex: 0,
      tabLinkWidth: 0,
      tabLinkHeight: 50,
      navigating: false
    };
  },
  computed: {
    tabCount() {
      return this.tabs.length;
    },
    linkWidth() {
      let width = 100;
      if (this.tabCount > 0) {
        width = 100 / this.tabCount;
      }
      if (this.vertical) {
        width = 100;
      }
      return { width: `${width}%` };
    },
    activeTab() {
      return this.tabs[this.activeTabIndex];
    },
    stepPercentage() {
      return (1 / (this.tabCount * 2)) * 100;
    },
    progress() {
      let percentage = 0;
      if (this.activeTabIndex > 0) {
        let stepsToAdd = 1;
        let stepMultiplier = 2;
        percentage =
          this.stepPercentage *
          (this.activeTabIndex * stepMultiplier + stepsToAdd);
      } else {
        percentage = this.stepPercentage;
      }
      return percentage;
    }
  },
  methods: {
    tabIndex(index){
      this.activeTabIndex = index;
    },
    addTab(tab) {
      const index = this.$slots.default.indexOf(tab.$vnode);
      let tabTitle = tab.title || '';
      tab.tabId = `${tabTitle.replace(/ /g, '')}${index}`;
      if (!this.activeTab && index === 0) {
        tab.active = true;
        tab.checked = true;
      }
      if (this.activeTab === tab.name) {
        tab.active = true;
        tab.checked = true;
      }
      this.tabs.splice(index, 0, tab);
    },
    removeTab(tab) {
      const tabs = this.tabs;
      const index = tabs.indexOf(tab);
      if (index > -1) {
        tabs.splice(index, 1);
      }
    },
    validate(tab) {
      let tabToValidate = tab || this.activeTab;
      let beforeChange = tabToValidate.beforeChange;
      if (beforeChange) {
        return Promise.resolve(beforeChange())
          .then(res => {
            this.activeTab.hasError = res ? false : true;
            return res;
          })
          .catch(() => {
            this.activeTab.hasError = true;
          });
      } else {
        return Promise.resolve(true);
      }
    },
    scrollToTop(){
      const root = document.getElementById('root');
      root.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    },
    async nextTab() {
      if (this.navigating) return false
      this.scrollToTop()
      this.navigating = true
      let isValid = await this.validate();
      if (isValid && this.activeTabIndex < this.tabCount - 1) {
        this.activeTabIndex++;
      }
      this.navigating = false
      return isValid;
    },
    prevTab() {
      this.activeTabIndex--;
      this.scrollToTop()
    },
    async navigateToTab(index) {
      if (this.navigating) return
      this.scrollToTop()
      if (this.tabs[index].checked) {
        if (index > this.activeTabIndex) {
          this.navigating = true
          // Validate each intermediate tab without changing the visible tab
          let currentIndex = this.activeTabIndex
          while (currentIndex < index) {
            let tab = this.tabs[currentIndex]
            if (tab.beforeChange) {
              let res = await Promise.resolve(tab.beforeChange())
              if (!res) {
                // Validation failed — stop at the failing tab
                if (currentIndex !== this.activeTabIndex) {
                  this.activeTabIndex = currentIndex
                }
                this.navigating = false
                return
              }
            }
            tab.checked = true
            currentIndex++
          }
          // All validations passed — jump directly to target
          this.activeTabIndex = index
          this.navigating = false
        } else {
          this.activeTabIndex = index;
        }
      }
    },
    onResize() {
      let tabLinks = document.getElementsByClassName('wizard-tab-link');
      if (tabLinks.length > 0 && this.tabCount > 0) {
        let { clientWidth, clientHeight } = tabLinks[0];
        this.tabLinkWidth = clientWidth;
        this.tabLinkHeight = clientHeight;
      }
    }
  },
  mounted() {
    this.activeTabIndex = this.startIndex;
    this.$nextTick(() => {
      this.tabs[this.activeTabIndex].active = true;
      this.tabs[this.activeTabIndex].checked = true;
      this.onResize();
    });
    window.addEventListener(
      'resize',
      () => {
        throttle(this.onResize, 40);
      },
      false
    );
  },
  watch: {
    activeTabIndex(newValue, oldValue) {
      if (newValue !== oldValue) {
        let oldTab = this.tabs[oldValue];
        let newTab = this.tabs[newValue];
        oldTab.active = false;
        newTab.active = true;

        if (!newTab.checked) {
          newTab.checked = true;
        }
        this.$emit('tab-change', oldTab, newTab);
        this.$emit('update:startIndex', newValue);
      }
    }
  }
};
</script>
<style lang="scss">
/* Tab content animation */
.tab-content {
  display: flex; // to avoid horizontal scroll when animating
  .tab-pane {
    display: block;
    animation: fadeIn 0.5s;
    width: 100%;
  }
}

/**
      Extra niceties. Display error tabs and disable navigation unvisited tabs
     */
.wizard-navigation .nav-link {
  &.active,
  &.checked {
    cursor: pointer;
  }
}

.disabled-wizard-link {
  cursor: not-allowed;
}

/* ================================================
   Wizard footer navigation buttons
   ================================================ */
.wizard-footer-buttons {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  gap: 12px;
}

.wiz-btn-spacer {
  flex: 1;
}

.wiz-btn-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Shared overrides on top of base-button */
.wiz-btn.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  font-size: 13px;
  font-weight: 500;
  text-transform: none;
  border-radius: 6px;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;

  i {
    font-size: 12px;
    line-height: 1;
  }
}

/* Previous — ghost outline, visually recessive */
.wiz-btn--prev.btn.btn-primary {
  background: transparent !important;
  background-image: none !important;
  border-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.6);
  box-shadow: none;

  &:hover,
  &:focus {
    background: rgba(255, 255, 255, 0.06) !important;
    border-color: rgba(255, 255, 255, 0.25);
    color: rgba(255, 255, 255, 0.85);
    box-shadow: none;
  }

  &:active {
    background: rgba(255, 255, 255, 0.1) !important;
  }
}

/* Next & Finish — use base-button's primary gradient as-is,
   just add a subtle shadow for depth */
.wiz-btn--next.btn.btn-primary,
.wiz-btn--finish.btn.btn-primary {
  box-shadow: 0 2px 8px rgba(29, 140, 248, 0.3);

  &:hover,
  &:focus {
    box-shadow: 0 4px 14px rgba(29, 140, 248, 0.4);
  }
}

/* ---- Light mode ---- */
.white-content .wiz-btn--prev.btn.btn-primary {
  border-color: rgba(0, 0, 0, 0.12);
  color: rgba(0, 0, 0, 0.5);

  &:hover,
  &:focus {
    background: rgba(0, 0, 0, 0.04) !important;
    border-color: rgba(0, 0, 0, 0.2);
    color: rgba(0, 0, 0, 0.7);
  }

  &:active {
    background: rgba(0, 0, 0, 0.07) !important;
  }
}

/* Button loading spinner */
.wiz-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: wiz-spin 0.6s linear infinite;
}

@keyframes wiz-spin {
  to { transform: rotate(360deg); }
}

/* ---- Responsive ---- */
@media (max-width: 575.98px) {
  .wizard-footer-buttons {
    padding: 0 8px;
    gap: 8px;
  }

  .wiz-btn.btn {
    padding: 10px 14px;
  }

  .wiz-btn-right {
    flex-wrap: nowrap;
  }
}
</style>
